import i18n from '@/utils/i18n';
import { Alert } from '@/experiences/sales-portal/domain/insights/blocs/types/alert';
import { AnalyticsAlertStatusType } from '@/types';
import { InsightsError } from '@/experiences/sales-portal/domain/insights/blocs/errors/insights-error';
import { partnerSupplier } from '@/utils/environment';
import { SalesPortalGetActiveAlertsDocument } from '@/experiences/sales-portal/domain/insights/blocs/documents/active-alerts.generated';
import { SalesPortalGetHiddenAlertsDocument } from '@/experiences/sales-portal/domain/insights/blocs/documents/hidden-alerts.generated';
import { useCallback, useMemo, useState } from 'react';
import { useSalesPortalUpdateAlertStatusesMutation } from '@/experiences/sales-portal/domain/insights/blocs/documents/alerts-update-status.generated';
import type { ApolloCache, NormalizedCacheObject } from '@apollo/client';
import type { DocumentNode } from 'graphql';
import type {
  SalesPortalGetActiveAlertsQuery,
  SalesPortalGetActiveAlertsQueryVariables,
  SalesPortalGetHiddenAlertsQuery,
  SalesPortalGetHiddenAlertsQueryVariables,
} from '@/types';

export interface UseAlertStatusMutatorResult {
  clearAlerts: (alerts: Alert[]) => Promise<void>;
  deleteAlert: (alert: Alert) => Promise<void>;
  hideAlert: (alert: Alert) => Promise<void>;
  loading: boolean;
  restoreAlert: (alert: Alert) => Promise<void>;
  visibleAlerts: Alert[];
}

interface HubCardEnvelope {
  alert_uuid: string;
}

type HubAlertFeedQuery =
  | SalesPortalGetActiveAlertsQuery
  | SalesPortalGetHiddenAlertsQuery;

type HubAlertFeedQueryVariables =
  | SalesPortalGetActiveAlertsQueryVariables
  | SalesPortalGetHiddenAlertsQueryVariables;

const moveAlertBetweenStatuses = (
  cache: ApolloCache<NormalizedCacheObject>,
  variables: HubAlertFeedQueryVariables,
  fromFeedQuery: DocumentNode,
  toFeedQuery: DocumentNode,
  alertUUID: string,
  toStatus: AnalyticsAlertStatusType,
): void => {
  const fromData = cache.readQuery<
    HubAlertFeedQuery,
    HubAlertFeedQueryVariables
  >({ query: fromFeedQuery, variables });

  const sourceCard = fromData?.getAllHubAlerts.card_data_json
    .map((serializedCard) => JSON.parse(serializedCard) as HubCardEnvelope)
    .find((card) => card.alert_uuid === alertUUID);
  if (!sourceCard) {
    return;
  }
  const moved = JSON.stringify({ ...sourceCard, alert_status: toStatus });

  cache.updateQuery<HubAlertFeedQuery, HubAlertFeedQueryVariables>(
    { query: fromFeedQuery, variables },
    (data) =>
      data
        ? {
            ...data,
            getAllHubAlerts: {
              ...data.getAllHubAlerts,
              card_data_json: data.getAllHubAlerts.card_data_json.filter(
                (serializedCard) =>
                  (JSON.parse(serializedCard) as HubCardEnvelope).alert_uuid !==
                  alertUUID,
              ),
            },
          }
        : undefined,
  );

  cache.updateQuery<HubAlertFeedQuery, HubAlertFeedQueryVariables>(
    { query: toFeedQuery, variables },
    (data) =>
      data
        ? {
            ...data,
            getAllHubAlerts: {
              ...data.getAllHubAlerts,
              card_data_json: [moved, ...data.getAllHubAlerts.card_data_json],
            },
          }
        : undefined,
  );
};

export const useAlertStatusMutator = (
  alerts: Alert[],
): UseAlertStatusMutatorResult => {
  const [updateAlertStatuses, { loading }] =
    useSalesPortalUpdateAlertStatusesMutation();
  const [removedUUIDs, setRemovedUUIDs] = useState<ReadonlySet<string>>(
    () => new Set<string>(),
  );

  const visibleAlerts = useMemo(
    () => alerts.filter((alert) => !removedUUIDs.has(alert.alertUUID)),
    [alerts, removedUUIDs],
  );

  const moveAlert = useCallback(
    async (
      alert: Alert,
      toStatus: AnalyticsAlertStatusType,
      fromFeedQuery: DocumentNode,
      toFeedQuery: DocumentNode,
    ) => {
      const cacheVariables = {
        businessOrganizationUUID: partnerSupplier.businessOrganizationUUID,
      };
      try {
        await updateAlertStatuses({
          optimisticResponse: {
            __typename: 'mutation_root',
            updateAnalyticsAlertsStatuses: {
              __typename: 'UpdateAnalyticsAlertsStatusResponse',
              updatedCount: 1,
            },
          },
          update: (cache) =>
            moveAlertBetweenStatuses(
              cache,
              cacheVariables,
              fromFeedQuery,
              toFeedQuery,
              alert.alertUUID,
              toStatus,
            ),
          variables: {
            alertStatus: toStatus,
            alertUUIDs: [alert.alertUUID],
            businessOrganizationUUID: partnerSupplier.businessOrganizationUUID,
          },
        });
      } catch (error) {
        throw new InsightsError(
          error instanceof Error
            ? error.message
            : i18n.t('insights_hub.unexpected_error'),
        );
      }
    },
    [updateAlertStatuses],
  );

  const removeAlerts = useCallback(
    async (alertUUIDs: string[]) => {
      setRemovedUUIDs((prev) => {
        const next = new Set(prev);
        alertUUIDs.forEach((uuid) => next.add(uuid));
        return next;
      });

      try {
        await updateAlertStatuses({
          variables: {
            alertStatus: AnalyticsAlertStatusType.Delete,
            alertUUIDs,
            businessOrganizationUUID: partnerSupplier.businessOrganizationUUID,
          },
        });
      } catch (error) {
        setRemovedUUIDs((prev) => {
          const next = new Set(prev);
          alertUUIDs.forEach((uuid) => next.delete(uuid));
          return next;
        });
        throw new InsightsError(
          error instanceof Error
            ? error.message
            : i18n.t('insights_hub.unexpected_error'),
        );
      }
    },
    [updateAlertStatuses],
  );

  const hideAlert = useCallback(
    (alert: Alert) =>
      moveAlert(
        alert,
        AnalyticsAlertStatusType.Hidden,
        SalesPortalGetActiveAlertsDocument,
        SalesPortalGetHiddenAlertsDocument,
      ),
    [moveAlert],
  );

  const restoreAlert = useCallback(
    (alert: Alert) =>
      moveAlert(
        alert,
        AnalyticsAlertStatusType.Active,
        SalesPortalGetHiddenAlertsDocument,
        SalesPortalGetActiveAlertsDocument,
      ),
    [moveAlert],
  );

  const deleteAlert = useCallback(
    (alert: Alert) => removeAlerts([alert.alertUUID]),
    [removeAlerts],
  );

  const clearAlerts = useCallback(
    (toClear: Alert[]) => removeAlerts(toClear.map((alert) => alert.alertUUID)),
    [removeAlerts],
  );

  return {
    clearAlerts,
    deleteAlert,
    hideAlert,
    loading,
    restoreAlert,
    visibleAlerts,
  };
};
